import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { COMMON_TIMEZONES } from "@/lib/schedule-utils";
import { useState } from "react";

interface TimezoneSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

export function TimezoneSelector({ value, onChange }: TimezoneSelectorProps) {
  useLanguage();
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');
  const detectedTimezone = typeof Intl !== 'undefined' 
    ? Intl.DateTimeFormat().resolvedOptions().timeZone 
    : 'America/Sao_Paulo';

  const handleModeChange = (newMode: 'auto' | 'manual') => {
    setMode(newMode);
    if (newMode === 'auto') {
      onChange(detectedTimezone);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>{translateInterfaceText("Fuso Horário")}</Label>
        <p className="text-sm text-muted-foreground mt-1">
          {translateInterfaceText("Define o horário de referência para a sessão")}</p>
      </div>

      <RadioGroup value={mode} onValueChange={handleModeChange}>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="auto" id="auto" />
          <Label htmlFor="auto" className="font-normal cursor-pointer">
            {translateInterfaceText("Detectado automaticamente")}</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="manual" id="manual" />
          <Label htmlFor="manual" className="font-normal cursor-pointer">
            {translateInterfaceText("Selecionar manualmente")}</Label>
        </div>
      </RadioGroup>

      {mode === 'auto' && (
        <Badge variant="secondary" className="mt-2">
          {translateInterfaceText("Fuso atual:")}{" "}{detectedTimezone}
        </Badge>
      )}

      {mode === 'manual' && (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={translateInterfaceText("Selecione o fuso horário")} />
          </SelectTrigger>
          <SelectContent>
            {COMMON_TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
